import { TimesheetColumnDef } from '@app/shared/interfaces/vendor-management-model';
import { caseInsensitiveStringComparator } from '@utils/utils';

export interface TimeSheet {
  employeeId: string;
  employeeName: string;
  employeeStatus?: string;
  jobTitle: string;
  location: string;
  l3ManagerName?: string;
  workingHrs: number;
  billableHrs: number;
  leavesAvailed: number;
  vendorName: string;
  status: string;
  selected?: boolean;
  selectable?: boolean;
  reportingManager?: string;
  projectName?: string;
  calendar?: string;
  lastWorkingDay?: string;
  submittedBy?: string;
  submittedDate?: string;
}

export const OPS_TIMESHEET_CONFIG = {
  HEADERS: {
    SELECT: '',
    EMPLOYEE_ID: 'Employee ID',
    NAME: 'Name',
    EMPLOYEE_STATUS: 'Employee Status',
    VENDOR: 'Vendor',
    LOCATION: 'Location',
    DNT_LEADER: 'D&T Leader',
    REPORTING_MANAGER: 'Reporting Manager',
    WORKING_HRS: 'Total Working Hrs',
    LEAVES_AVAILED: 'Leaves Availed',
    BILLABLE_HRS: 'Billable Hrs',
    STATUS: 'Status',
    CALENDAR: 'Calendar',
    L3_MANAGER: 'L3 Manager',
    L4_MANAGER: 'L4 Manager',
    L5_MANAGER: 'L5 Manager',
    SEND_MAIL: 'Action',
    PAID_LEAVES: 'Paid Leaves',
    UNPAID_LEAVES: 'Unpaid Leaves',
    LAST_WORKING_DAY: 'Last Working Day',
    APPROVED_BY: 'Approved By',
    APPROVED_DATE: 'Approved Date',
    SUBMITTED_BY: 'Submitted By',
    SUBMITTED_DATE: 'Submitted Date',
  },
  FIELDS: {
    SELECT: 'select',
    EMPLOYEE_ID: 'employeeId',
    EMPLOYEE_NAME: 'employeeName',
    EMPLOYEE_STATUS: 'employeeStatus',
    VENDOR_NAME: 'vendorName',
    LOCATION: 'location',
    DNT_LEADER: 'l3ManagerName',
    REPORTING_MANAGER: 'reportingManager',
    WORKING_HRS: 'workingHrs',
    BILLABLE_HRS: 'billableHrs',
    LEAVES_AVAILED: 'leavesAvailed',
    STATUS: 'status',
    CALENDAR: 'calendar',
    L3_MANAGER_NAME: 'l3ManagerName',
    L4_MANAGER_NAME: 'l4ManagerName',
    L5_MANAGER_NAME: 'l5ManagerName',
    SEND_MAIL: 'sendMail',
    PAID_LEAVES: 'paidLeaves',
    UNPAID_LEAVES: 'unpaidLeaves',
    LAST_WORKING_DAY: 'lastWorkingDay',
    APPROVED_BY: 'reviewedBy',
    APPROVED_DATE: 'reviewedDate',
    SUBMITTED_BY: 'submittedBy',
    SUBMITTED_DATE: 'submittedDate',
  },
  CELL_TEMPLATES: {
    CUSTOM_SELECT_COLUMN: 'customSelectColumn',
    CUSTOM_NAME_COLUMN: 'customNameColumn',
    CUSTOM_EMPLOYEE_STATUS_COLUMN: 'customEmployeeStatusColumn',
    CUSTOM_CALENDAR_COLUMN: 'customCalenderColumn',
    CUSTOM_BILLABLE_HRS_COLUMN: 'customBillableHrsColumn',
    CUSTOM_WORKING_HRS_COLUMN: 'customWorkinghrsColumn',
    CUSTOM_STATUS_COLUMN: 'customStatusColumn',
    CUSTOM_SEND_MAIL_COLUMN: 'customSendMailColumn'
  }
} as const;

// Reusable configuration objects
const FILTER_CONFIG = { hideMenu: true };
const SORT_ENABLED_CONFIG = { enabled: true };
const SORT_DISABLED_CONFIG = { enabled: false };

const baseColumnDefs: TimesheetColumnDef[] = [
  {
      field: OPS_TIMESHEET_CONFIG.FIELDS.EMPLOYEE_ID,
      headerName: OPS_TIMESHEET_CONFIG.HEADERS.EMPLOYEE_ID,
      filterParams: FILTER_CONFIG,
      sortParams: SORT_ENABLED_CONFIG,
      sortComparator: caseInsensitiveStringComparator,
      customCellTemplate: OPS_TIMESHEET_CONFIG.CELL_TEMPLATES.CUSTOM_NAME_COLUMN,
    },
  {
      field: OPS_TIMESHEET_CONFIG.FIELDS.EMPLOYEE_NAME,
      headerName: OPS_TIMESHEET_CONFIG.HEADERS.NAME,
      filterParams: FILTER_CONFIG,
      sortParams: SORT_ENABLED_CONFIG,
      sortComparator: caseInsensitiveStringComparator,
      customCellTemplate: OPS_TIMESHEET_CONFIG.CELL_TEMPLATES.CUSTOM_NAME_COLUMN,
    },
    {
    field: OPS_TIMESHEET_CONFIG.FIELDS.EMPLOYEE_STATUS,
    headerName: OPS_TIMESHEET_CONFIG.HEADERS.EMPLOYEE_STATUS,
    filterParams: FILTER_CONFIG,
    sortParams: SORT_ENABLED_CONFIG,
    sortComparator: caseInsensitiveStringComparator,
    customCellTemplate: OPS_TIMESHEET_CONFIG.CELL_TEMPLATES.CUSTOM_EMPLOYEE_STATUS_COLUMN,
  },
    {
      field: OPS_TIMESHEET_CONFIG.FIELDS.VENDOR_NAME,
      headerName: OPS_TIMESHEET_CONFIG.HEADERS.VENDOR,
      filterParams: FILTER_CONFIG,
      sortParams: SORT_ENABLED_CONFIG,
      sortComparator: caseInsensitiveStringComparator,
    },
    
    {
      field: OPS_TIMESHEET_CONFIG.FIELDS.DNT_LEADER,
      headerName: OPS_TIMESHEET_CONFIG.HEADERS.DNT_LEADER,
      filterParams: FILTER_CONFIG,
      sortParams: SORT_ENABLED_CONFIG,
      sortComparator: caseInsensitiveStringComparator,
    },
    {
      field: OPS_TIMESHEET_CONFIG.FIELDS.REPORTING_MANAGER,
      headerName: OPS_TIMESHEET_CONFIG.HEADERS.REPORTING_MANAGER,
      filterParams: FILTER_CONFIG,
      sortParams: SORT_ENABLED_CONFIG,
      sortComparator: caseInsensitiveStringComparator,
    },
    {
      field: OPS_TIMESHEET_CONFIG.FIELDS.LOCATION,
      headerName: OPS_TIMESHEET_CONFIG.HEADERS.LOCATION,
      filterParams: FILTER_CONFIG,
      sortParams: SORT_ENABLED_CONFIG,
      sortComparator: caseInsensitiveStringComparator,
    },
    {
      field: OPS_TIMESHEET_CONFIG.FIELDS.WORKING_HRS,
      headerName: OPS_TIMESHEET_CONFIG.HEADERS.WORKING_HRS,
      filterParams: FILTER_CONFIG,
      sortParams: SORT_ENABLED_CONFIG,
      customCellTemplate: OPS_TIMESHEET_CONFIG.CELL_TEMPLATES.CUSTOM_WORKING_HRS_COLUMN,
    },
      {
      field: OPS_TIMESHEET_CONFIG.FIELDS.LEAVES_AVAILED,
      headerName: OPS_TIMESHEET_CONFIG.HEADERS.LEAVES_AVAILED,
      filterParams: FILTER_CONFIG,
      sortParams: SORT_ENABLED_CONFIG,
    },
    {
      field: OPS_TIMESHEET_CONFIG.FIELDS.BILLABLE_HRS,
      headerName: OPS_TIMESHEET_CONFIG.HEADERS.BILLABLE_HRS,
      filterParams: FILTER_CONFIG,
      sortParams: SORT_ENABLED_CONFIG,
      customCellTemplate: OPS_TIMESHEET_CONFIG.CELL_TEMPLATES.CUSTOM_BILLABLE_HRS_COLUMN,
    },
    {
      field: OPS_TIMESHEET_CONFIG.FIELDS.STATUS,
      headerName: OPS_TIMESHEET_CONFIG.HEADERS.STATUS,
      filterParams: FILTER_CONFIG,
      sortParams: SORT_ENABLED_CONFIG,
      sortComparator: caseInsensitiveStringComparator,
      customCellTemplate: OPS_TIMESHEET_CONFIG.CELL_TEMPLATES.CUSTOM_STATUS_COLUMN,
    },
    {
      field: OPS_TIMESHEET_CONFIG.FIELDS.CALENDAR,
      headerName: OPS_TIMESHEET_CONFIG.HEADERS.CALENDAR,
      filterParams: FILTER_CONFIG,
      customCellTemplate: OPS_TIMESHEET_CONFIG.CELL_TEMPLATES.CUSTOM_CALENDAR_COLUMN,
    },
    {
      field: OPS_TIMESHEET_CONFIG.FIELDS.SEND_MAIL,
      headerName: OPS_TIMESHEET_CONFIG.HEADERS.SEND_MAIL,
      filterParams: FILTER_CONFIG,
      sortParams: SORT_DISABLED_CONFIG,
      minWidth: '180px',
      customCellTemplate: OPS_TIMESHEET_CONFIG.CELL_TEMPLATES.CUSTOM_SEND_MAIL_COLUMN,
    },
  ];

export const getColumnDefs = (rowData: TimeSheet[] = []): TimesheetColumnDef[] => {
  const selectColumn = {
    field: OPS_TIMESHEET_CONFIG.FIELDS.SELECT,
    dataType: OPS_TIMESHEET_CONFIG.FIELDS.SELECT,
    headerName: OPS_TIMESHEET_CONFIG.HEADERS.SELECT,
    cellRenderer: null,
    checkboxSelection: false,
    customCellTemplate: OPS_TIMESHEET_CONFIG.CELL_TEMPLATES.CUSTOM_SELECT_COLUMN,
    hide: false,
  };

  return [selectColumn, ...baseColumnDefs];
};

export const exportColumnDefs = [
  {
    field: OPS_TIMESHEET_CONFIG.FIELDS.VENDOR_NAME,
    headerName: OPS_TIMESHEET_CONFIG.HEADERS.VENDOR,
  },
  {
    field: OPS_TIMESHEET_CONFIG.FIELDS.EMPLOYEE_ID,
    headerName: OPS_TIMESHEET_CONFIG.HEADERS.EMPLOYEE_ID,
  },
  {
    field: OPS_TIMESHEET_CONFIG.FIELDS.EMPLOYEE_NAME,
    headerName: OPS_TIMESHEET_CONFIG.HEADERS.NAME,
  },
  {
    field: OPS_TIMESHEET_CONFIG.FIELDS.WORKING_HRS,
    headerName: OPS_TIMESHEET_CONFIG.HEADERS.WORKING_HRS,
  },
  {
    field: OPS_TIMESHEET_CONFIG.FIELDS.LEAVES_AVAILED,
    headerName: OPS_TIMESHEET_CONFIG.HEADERS.LEAVES_AVAILED,
  },
  {
    field: OPS_TIMESHEET_CONFIG.FIELDS.PAID_LEAVES,
    headerName: OPS_TIMESHEET_CONFIG.HEADERS.PAID_LEAVES,
  },
  {
    field: OPS_TIMESHEET_CONFIG.FIELDS.UNPAID_LEAVES,
    headerName: OPS_TIMESHEET_CONFIG.HEADERS.UNPAID_LEAVES,
  },
  {
    field: OPS_TIMESHEET_CONFIG.FIELDS.BILLABLE_HRS,
    headerName: OPS_TIMESHEET_CONFIG.HEADERS.BILLABLE_HRS,
  },
  {
    field: OPS_TIMESHEET_CONFIG.FIELDS.STATUS,
    headerName: OPS_TIMESHEET_CONFIG.HEADERS.STATUS,
  },
  {
    field: OPS_TIMESHEET_CONFIG.FIELDS.L3_MANAGER_NAME,
    headerName: OPS_TIMESHEET_CONFIG.HEADERS.L3_MANAGER,
  },
  {
    field: OPS_TIMESHEET_CONFIG.FIELDS.L4_MANAGER_NAME,
    headerName: OPS_TIMESHEET_CONFIG.HEADERS.L4_MANAGER,
  },
  {
    field: OPS_TIMESHEET_CONFIG.FIELDS.L5_MANAGER_NAME,
    headerName: OPS_TIMESHEET_CONFIG.HEADERS.L5_MANAGER,
  },
  {
    field: OPS_TIMESHEET_CONFIG.FIELDS.LOCATION,
    headerName: OPS_TIMESHEET_CONFIG.HEADERS.LOCATION,
  },
  {
    field: OPS_TIMESHEET_CONFIG.FIELDS.DNT_LEADER,
    headerName: OPS_TIMESHEET_CONFIG.HEADERS.DNT_LEADER,
  },
  {
    field: OPS_TIMESHEET_CONFIG.FIELDS.REPORTING_MANAGER,
    headerName: OPS_TIMESHEET_CONFIG.HEADERS.REPORTING_MANAGER,
  },
  {
    field: OPS_TIMESHEET_CONFIG.FIELDS.LAST_WORKING_DAY,
    headerName: OPS_TIMESHEET_CONFIG.HEADERS.LAST_WORKING_DAY,
  },
  {
    field: OPS_TIMESHEET_CONFIG.FIELDS.APPROVED_BY,
    headerName: OPS_TIMESHEET_CONFIG.HEADERS.APPROVED_BY,
  },
  {
    field: OPS_TIMESHEET_CONFIG.FIELDS.APPROVED_DATE,
    headerName: OPS_TIMESHEET_CONFIG.HEADERS.APPROVED_DATE,
  },
  {
    field: OPS_TIMESHEET_CONFIG.FIELDS.SUBMITTED_BY,
    headerName: OPS_TIMESHEET_CONFIG.HEADERS.SUBMITTED_BY,
  },
  {
    field: OPS_TIMESHEET_CONFIG.FIELDS.SUBMITTED_DATE,
    headerName: OPS_TIMESHEET_CONFIG.HEADERS.SUBMITTED_DATE,
  },
];

export const columnDefs: TimesheetColumnDef[] = getColumnDefs();

export const fontSize_calender = 1.3;
